import { useEffect, useMemo, useState } from 'react';
import {
  KNOWN_UPSTREAM_PROTOCOLS,
  MAX_DISCOVERY_MODEL_ID_LENGTH,
  MAX_DISCOVERY_MODEL_LIST_ITEMS,
} from '@nadoutong/contracts';
import type {
  DiscoverUpstreamCapabilitiesRequest,
  UpstreamModelCapabilityRecord,
  UpstreamProtocol,
} from '@nadoutong/contracts';
import { modelDiscoveryServerError } from '../ModelDiscoveryFields.tsx';
import { coerceErrorMessage, useToast } from '../Toast.tsx';
import { useT } from '../../i18n/index.ts';
import { Button, Dialog, FormField, Select, Textarea } from '../../ui/index.ts';
import type { SelectOption } from '../../ui/index.ts';

/**
 * Manual model import dialog (issue #1658 part B).
 *
 * When an upstream's `model_discovery.mode` is `manual`, the gateway
 * performs no remote list request — the operator pastes model ids here.
 * The discover endpoint's per-call cap is owned by the contract, so a
 * larger paste is split into capped batches submitted serially. The
 * gateway's manual branch performs no `mark_missing`, so each committed
 * batch is additive and a mid-run failure keeps every already-submitted
 * batch — the error copy says which batch failed and what survived.
 */

const MAX_MODELS_PER_BATCH = MAX_DISCOVERY_MODEL_LIST_ITEMS;
const MAX_MODEL_ID_LENGTH = MAX_DISCOVERY_MODEL_ID_LENGTH;

const PROTOCOLS: readonly UpstreamProtocol[] = KNOWN_UPSTREAM_PROTOCOLS;

export function parseManualModels(text: string): readonly string[] {
  const seen = new Set<string>();
  const models: string[] = [];
  for (const raw of text.split(/[\n,]/)) {
    const model = raw.trim();
    if (model.length === 0 || seen.has(model)) continue;
    seen.add(model);
    models.push(model);
  }
  return models;
}

function chunkModels(
  models: readonly string[],
  size: number,
): readonly (readonly string[])[] {
  const chunks: (readonly string[])[] = [];
  for (let index = 0; index < models.length; index += size) {
    chunks.push(models.slice(index, index + size));
  }
  return chunks;
}

interface BatchProgress {
  readonly current: number;
  readonly total: number;
}

interface ManualDiscoverDialogProps {
  readonly open: boolean;
  readonly channelId: string;
  readonly channelSlug: string;
  readonly onClose: () => void;
  readonly discoverAsync: (
    body: DiscoverUpstreamCapabilitiesRequest,
  ) => Promise<UpstreamModelCapabilityRecord[]>;
}

export function ManualDiscoverDialog({
  open,
  channelId,
  channelSlug,
  onClose,
  discoverAsync,
}: ManualDiscoverDialogProps): JSX.Element {
  const t = useT();
  const toast = useToast();
  const [text, setText] = useState('');
  const [protocol, setProtocol] = useState<'' | UpstreamProtocol>('');
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState<BatchProgress | null>(null);
  const [error, setError] = useState<string | null>(null);

  // A fresh open targets a fresh channel: start from a clean draft. On a
  // failed batch the dialog stays open with all state intact so the
  // operator can retry or trim the paste.
  useEffect(() => {
    if (open) {
      setText('');
      setProtocol('');
      setSubmitting(false);
      setProgress(null);
      setError(null);
    }
  }, [open, channelId]);

  const models = useMemo(() => parseManualModels(text), [text]);
  const batchCount = Math.ceil(models.length / MAX_MODELS_PER_BATCH);

  const protocolOptions: readonly SelectOption[] = PROTOCOLS.map((value) => ({
    value,
    label: value,
  }));

  function close(): void {
    if (submitting) return;
    onClose();
  }

  async function handleSubmit(): Promise<void> {
    // The button is disabled while submitting, but a keyboard-driven double
    // activation can still land two calls before React re-renders; a second
    // run would replay batch 1 against the same channel.
    if (submitting) return;
    setError(null);
    if (models.length === 0) {
      setError(t('upstream_detail.routing.manual.error.empty'));
      return;
    }
    if (models.some((model) => model.length > MAX_MODEL_ID_LENGTH)) {
      setError(t('upstream_detail.routing.manual.error.too_long'));
      return;
    }
    if (protocol === '') {
      setError(t('upstream_detail.routing.manual.error.protocol'));
      return;
    }
    const chunks = chunkModels(models, MAX_MODELS_PER_BATCH);
    setSubmitting(true);
    let submitted = 0;
    // Iterating the values keeps every batch reachable — an indexed read
    // needs an undefined guard that can only be satisfied by abandoning the
    // run silently. `completed` carries the "batch i of k" wording instead.
    let completed = 0;
    for (const chunk of chunks) {
      setProgress({ current: completed + 1, total: chunks.length });
      try {
        await discoverAsync({
          channel_id: channelId,
          protocol,
          models: [...chunk],
        });
        submitted += chunk.length;
        completed += 1;
      } catch (err) {
        const message = modelDiscoveryServerError(t, err) ?? coerceErrorMessage(err);
        setError(
          t('upstream_detail.routing.manual.partial', {
            failed: completed + 1,
            total: chunks.length,
            succeeded: completed,
            models: submitted,
            message,
          }),
        );
        setSubmitting(false);
        setProgress(null);
        return;
      }
    }
    setSubmitting(false);
    setProgress(null);
    toast.success(
      t('upstream_detail.routing.toast.models_loaded', { count: models.length }),
    );
    onClose();
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) close();
      }}
      title={t('upstream_detail.routing.manual.title', { channel: channelSlug })}
      description={t('upstream_detail.routing.manual.body')}
      size="lg"
      closeLabel={t('common.close')}
      testId="manual-discover-dialog"
      footer={
        <>
          <Button variant="ghost" onClick={close} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={() => void handleSubmit()}
            disabled={submitting || models.length === 0 || protocol === ''}
            loading={submitting}
            data-testid="manual-discover-submit"
          >
            {submitting
              ? t('upstream_detail.routing.manual.submitting')
              : t('upstream_detail.routing.manual.submit')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <FormField
          label={t('upstream_detail.routing.manual.models.label')}
          fieldId="manual-discover-models"
          required
        >
          <Textarea
            id="manual-discover-models"
            value={text}
            onChange={(e) => setText(e.currentTarget.value)}
            placeholder={'claude-sonnet-4-6\nclaude-opus-4-8'}
            rows={10}
            disabled={submitting}
            mono
            data-testid="manual-discover-models"
          />
        </FormField>
        <FormField
          label={t('upstream_detail.routing.manual.protocol.label')}
          fieldId="manual-discover-protocol"
          required
        >
          <Select
            id="manual-discover-protocol"
            value={protocol}
            onValueChange={(value) => {
              const next = PROTOCOLS.find((candidate) => candidate === value);
              if (next !== undefined) setProtocol(next);
            }}
            options={protocolOptions}
            placeholder={t('upstream_detail.routing.manual.protocol.placeholder')}
            disabled={submitting}
            ariaLabel={t('upstream_detail.routing.manual.protocol.label')}
            mono
            testId="manual-discover-protocol"
          />
        </FormField>
        <p className="m-0 text-dense text-paper-mid" data-testid="manual-discover-parsed">
          {t('upstream_detail.routing.manual.parsed', { count: models.length })}
          {batchCount > 1
            ? ` ${t('upstream_detail.routing.manual.batches', { count: batchCount })}`
            : ''}
        </p>
        {progress !== null ? (
          <p
            className="m-0 rounded-lg bg-well px-4 py-3 text-dense text-paper"
            role="status"
            data-testid="manual-discover-progress"
          >
            {t('upstream_detail.routing.manual.progress', {
              current: progress.current,
              total: progress.total,
            })}
          </p>
        ) : null}
        {error !== null ? (
          <p
            className="m-0 text-label text-bad"
            role="alert"
            data-testid="manual-discover-error"
          >
            {error}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}
